import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from 'src/app/core/services/auth';

import moment from 'moment';
import { Subject } from 'rxjs';

import { CSVFormatterService } from '../../../core/services/csv-formatter';
import { ServiceGroup } from '../../../models/service-group.model';
import { Service } from '../../../models/service.model';

import { SlideInOutAnimation } from '../../inventory.animations';
import { LocationNameComponent } from '../location-name/location-name.component';

const validGroupByValues: Record<string, string> = {
  Location: 'Z_Location__r.Name',
  Service: 'RecordType.Name',
  Status: 'Status__c',
};

@Component({
  selector: 'app-inventory-list',
  templateUrl: './inventory-list.component.html',
  styleUrls: ['./inventory-list.component.scss'],
  animations: [SlideInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InventoryListComponent
  implements OnInit, OnChanges, DoCheck, OnDestroy
{
  @Input() browserSize: string;

  @Input() services: Service[] = [];

  @Input() isLoading: boolean;

  @Input() isLoaded: boolean;

  @Input() groupedServices: ServiceGroup[] = [];

  @Input() activeServices: Service[];

  @Input() selectedService: Service;

  @Output() setSelectedService = new EventEmitter();

  @Input() isServicesInProgress: boolean;

  @Input() groupByFormControl: UntypedFormControl;

  @Input() isAscFormControl: UntypedFormControl;

  @Output() setLoadingIndicator = new EventEmitter();

  @Input() locationsFilterOptions: string[];

  @Input() locationsFilterFormControl: UntypedFormControl;

  selectedLocationFilters: string[] = [];

  @Input() serviceFilterOptions: string[];

  @Input() serviceFilterFormControl: UntypedFormControl;

  selectedServiceFilters: string[] = [];

  @Input() statusFilterOptions: string[];

  @Input() statusFilterFormControl: UntypedFormControl;

  selectedStatusFilters: string[] = [];

  formattedCSVData = [];

  @Input() totalSelectedFilters = 0;

  @ViewChild('headerContainer', { static: false }) headerContainer: ElementRef;

  animationState = 'out';

  headerHeight: number;

  inventoryListHeight: number;

  @Input() searchTermControl: UntypedFormControl;

  page = 1;

  authService = inject(AuthService);

  route = inject(Router);

  activatedRoute = inject(ActivatedRoute);

  cdr = inject(ChangeDetectorRef);

  dialog = inject(MatDialog);

  csvFormatter = inject(CSVFormatterService);

  private destroy$ = new Subject<void>();

  private serviceIdSet = new Set<string>();

  getGroupTotalMrc(services: Service[]): number {
    if (!Array.isArray(services) || services.length === 0) {
      return 0;
    }
    return services.reduce((sum, s) => sum + (Number(s.MRR__c) || 0), 0);
  }

  getGroupFilteredCount(services: Service[]): number {
    if (!Array.isArray(services) || services.length === 0) {
      return 0;
    }
    return services.length;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const groupBy = validGroupByValues[params.groupBy];
      if (groupBy) this.groupByFormControl.setValue(groupBy);
      if (params.services && this.searchTermControl) {
        this.searchTermControl.setValue(params.services);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('groupedServices' in changes && this.groupedServices?.length > 0) {
      this.serviceIdSet = new Set(
        this.groupedServices.flatMap((g) => g.services ?? []).map((s) => s.Id),
      );

      const isSelectionInList =
        !!this.selectedService?.Id &&
        this.serviceIdSet.has(this.selectedService.Id);

      if (!isSelectionInList) {
        this.onSelectService(this.groupedServices[0].services[0]);
      }
    }

    if ('services' in changes && this.services?.length > 0) {
      this.formatCSVData();
    }
  }

  ngDoCheck(): void {
    this.setInventoryListHeight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectService(service: Service): void {
    if (service.Id) this.setSelectedService.emit(service.Id);
  }

  onHeaderClick(service: Service): void {
    const serviceDiv = document.getElementsByClassName('inventory-viewport');
    serviceDiv[0].scrollTop = 0;
    this.onSelectService(service);
  }

  removeFilterSelection(key: string, value: string): void {
    let control: UntypedFormControl;

    switch (key) {
      case 'Z_Location__r.Name':
        control = this.locationsFilterFormControl;
        break;

      case 'RecordType.Name':
        control = this.serviceFilterFormControl;
        break;

      case 'Status__c':
        control = this.statusFilterFormControl;
        break;

      default:
        return; // unknown key, nothing to remove
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const newSelections = control.value.filter((s: string) => s !== value);

    control.setValue(newSelections);
    this.cdr.markForCheck();
  }

  onClickGroupBy(value: string): void {
    this.setLoadingIndicator.emit();
    const groupBy = validGroupByValues[value];

    if (this.groupByFormControl.value === groupBy) {
      this.isAscFormControl.setValue(
        Boolean(this.isAscFormControl.value) === false,
      );
    } else {
      this.route.navigate([], { queryParams: { groupBy: value } });
      this.groupByFormControl.setValue(groupBy);
      this.page = 1;
      this.cdr.markForCheck();
    }
  }

  formatCSVData(): void {
    if (!Array.isArray(this.services) || this.services.length === 0) {
      this.formattedCSVData = [];
      this.cdr.markForCheck();
      return;
    }

    const newCSVData = this.services.map((service) => {
      const elementInfo = this.csvFormatter.getElementInfo(service);
      const loc = service.Z_Location__r;

      return {
        NIT: service.Name,
        'Bundle Type': service.RecordType?.Name ?? '',
        Status: service.Status__c === 'New' ? 'Pending' : service.Status__c,
        Customer: service.Account__r?.Name ?? '',
        'Customer PON':
          (service?.Orders__r?.length || 0) > 0
            ? service.Orders__r[0].Customer_PON__c
            : '',
        'Customer Site ID': service?.Z_Location_Customer_Side_Id__c ?? '',
        'End User Company': service?.Z_Location_Company_Name__c ?? '',
        'Street Address': loc?.Street_1__c
          ? `${loc.Street_1__c} ${loc.Street_2__c || ''}`
          : '',
        City: loc?.City__c ?? '',
        State: loc?.State__c ?? '',
        ZIP: loc?.Zip_Code__c ?? '',
        'Local Contact': service?.localContacts ?? '',
        'Sales Representative': service?.Sales_Rep__r?.Name ?? '',
        'Account Manager': service?.Account__r?.Account_Manager__r?.Name ?? '',
        'Connector Type': service.Connector_Type_Z_Location__c,
        Bandwidth: service.Asymmetrical_Bandwidth__c
          ? `${service.Bandwidth_Downstream__c} / ${service.Bandwidth_Upstream__c}`
          : service.Bandwidth_Downstream__c,
        Term: service?.Term__c ?? '',
        'End of Term': service?.Current_Term_End_Date__c ?? '',
        Provider:
          (service?.Vendor__r?.Display_Alias__c as string) ||
          (service?.Vendor__r?.Name as string) ||
          '',
        'Provider Circuit ID': elementInfo?.vendorCircuitId ?? '',
        'WAN IP Block': elementInfo?.wanIpBlock ?? '',
        'WAN Mask': elementInfo?.wanMask ?? '',
        'LAN IP Block': elementInfo?.lanIpBlock ?? '',
        'LAN Mask': elementInfo?.lanMask ?? '',
        Dmarc: elementInfo?.dmarcZLoc ?? '',
        Equipment: elementInfo?.equipmentName ?? '',
        'Start of Service': service?.Start_Of_Service__c ?? '',
        'End of Service': service?.End_Of_Service__c ?? '',
        'Activation Completed Date':
          service?.Activation_Date__c != null
            ? moment(service.Activation_Date__c).format('L')
            : '',
        'Billing Initiated Date': service?.First_Invoice__c ?? '',
        MRC: service?.MRR__c ?? '',
      };
    });

    this.formattedCSVData = newCSVData;
    this.cdr.markForCheck();
  }

  onEditLocationName(service: Service): void {
    // open modal
    const dialogConfig = {
      autoFocus: true,
      minHeight: this.browserSize === 'small' ? '315px' : '380px',
      width: this.browserSize === 'small' ? 'calc(100vw - 20px)' : '650px',
      maxWidth: '650px',
      data: {
        browserSize: this.browserSize,
        originPage: 'Service',
        locationId: service.Z_Location__c,
        locationAddress: service.Z_Location__r.Name,
        banLookupId: service.BanLookupId,
      },
      disableClose: false,
    };

    this.dialog.open(LocationNameComponent, dialogConfig);
  }

  toggleShowDiv(divName: string): void {
    if (divName === 'filter-wrapper') {
      this.animationState = this.animationState === 'out' ? 'in' : 'out';
      this.cdr.markForCheck();
      this.setInventoryListHeight();
    }
  }

  setInventoryListHeight(): void {
    setTimeout(() => {
      if (this.browserSize !== 'small') {
        this.headerHeight =
          (this.headerContainer?.nativeElement?.offsetHeight as number) ?? 0;
        const totalHeaderSpace = this.headerHeight + 110;
        const viewportHeight = window.innerHeight; // browser height

        this.inventoryListHeight = viewportHeight - totalHeaderSpace;
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  hasObjectLength(dataObj: object, expectedLength: number): boolean {
    return Object.keys(dataObj ?? {}).length === expectedLength;
  }
}
